import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Card } from "@/components/ui/Card";
import { Loader } from "@/components/ui/Loader";
import { formatNumber, formatPercent } from "@/utils/format";
import {
  fetchGoalTracking,
  saveManualGoalEntry,
  type GoalTrackingPayload,
} from "@/services/marketing";
import { cn } from "@/utils/cn";
import { GOAL_KINDS, goalKindLabel } from "./marketingGoalPlan";

interface Props {
  onDefineGoals: (month: string) => void;
  revision?: number;
}

const STATUS_LABEL = {
  no_ritmo: "No ritmo",
  atrasado: "Atrasado",
  batida: "Meta batida",
  sem_meta: "Sem meta",
} as const;

function currentMonth(): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Fortaleza",
    year: "numeric",
    month: "2-digit",
  }).format(new Date());
  return parts.slice(0, 7);
}

function monthTitle(month: string): string {
  const [year, m] = month.split("-");
  const names = ["janeiro", "fevereiro", "março", "abril", "maio", "junho", "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"];
  return `${names[Number(m) - 1] ?? month} de ${year}`;
}

export function MarketingGoalsTracking({ onDefineGoals, revision = 0 }: Props) {
  const [month, setMonth] = useState(currentMonth);
  const [data, setData] = useState<GoalTrackingPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [kindFilter, setKindFilter] = useState<string>("all");
  const [weekFilter, setWeekFilter] = useState<string>("all");
  const [manualKind, setManualKind] = useState("stories");
  const [manualDate, setManualDate] = useState(`${currentMonth()}-01`);
  const [manualNote, setManualNote] = useState("");
  const [savingManual, setSavingManual] = useState(false);

  const load = async (target = month) => {
    setLoading(true);
    setError(null);
    try {
      setData(await fetchGoalTracking(target));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao carregar metas.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load(month);
    setManualDate(`${month}-01`);
  }, [month, revision]);

  const publications = useMemo(() => {
    const rows = data?.publications ?? [];
    return rows.filter((row) => {
      if (kindFilter !== "all" && row.kind !== kindFilter) return false;
      if (weekFilter !== "all") {
        const week = data?.weeks.find((item) => String(item.index) === weekFilter);
        if (!week || row.date < week.startDate || row.date > week.endDate) return false;
      }
      return true;
    });
  }, [data, kindFilter, weekFilter]);

  const chartKind = kindFilter === "all" ? "reels" : kindFilter;
  const maxBar = Math.max(
    1,
    ...(data?.days ?? []).map((day) => {
      const cell = day.kinds.find((item) => item.kind === chartKind);
      return Math.max(cell?.goal ?? 0, cell?.realized ?? 0);
    }),
  );

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <Input label="Mês" type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
        <p className="text-sm text-slate-500">Acompanhamento de {monthTitle(month)} · fuso America/Fortaleza</p>
        <Button variant="secondary" onClick={() => onDefineGoals(month)}>
          Definir metas
        </Button>
      </div>

      {loading && <Loader />}
      {error && <p className="text-sm text-red-600">{error}</p>}

      {!loading && data && !data.hasGoal && (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white px-4 py-8 text-center">
          <p className="text-sm text-slate-600">Nenhuma meta cadastrada para {monthTitle(month)}.</p>
          <Button className="mt-3" onClick={() => onDefineGoals(month)}>
            Definir metas
          </Button>
        </div>
      )}

      {!loading && data && data.hasGoal && (
        <>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            {data.cards.map((card) => (
              <Card
                key={card.kind}
                title={card.label}
                value={`${formatNumber(card.realized)} / ${formatNumber(card.goal)}`}
                subtitle={`${STATUS_LABEL[card.status]} · ${formatPercent(card.percent, 0)} · faltam ${formatNumber(card.remaining)} · projeção ${formatNumber(card.projection)}`}
                infoHint={`Até hoje: meta ${formatNumber(card.goalToDate)} · realizado ${formatNumber(card.realizedToDate)}. ${STATUS_LABEL[card.status]}.`}
              />
            ))}
          </div>

          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-2">Semana</th>
                  {GOAL_KINDS.map((kind) => (
                    <th key={kind} className="px-3 py-2">{goalKindLabel(kind)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.weeks.map((week) => (
                  <tr key={week.index} className={cn("border-t border-slate-100", week.current && "bg-brand-50")}>
                    <td className="px-3 py-2 font-medium text-slate-800">
                      {week.label}
                      {week.current ? " · atual" : ""}
                    </td>
                    {week.kinds.map((cell) => (
                      <td key={cell.kind} className="px-3 py-2 tabular-nums text-slate-700">
                        {formatNumber(cell.realized)}/{formatNumber(cell.goal)} · {formatPercent(cell.percent, 0)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="mb-3 flex items-center justify-between gap-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Meta x realizado por dia · {goalKindLabel(chartKind)}
              </p>
              <select
                className="rounded-lg border border-slate-200 px-2 py-1 text-sm"
                value={chartKind}
                onChange={(e) => setKindFilter(e.target.value)}
              >
                {GOAL_KINDS.map((kind) => (
                  <option key={kind} value={kind}>{goalKindLabel(kind)}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end gap-1 overflow-x-auto">
              {data.days.map((day) => {
                const cell = day.kinds.find((item) => item.kind === chartKind);
                const goal = cell?.goal ?? 0;
                const realized = cell?.realized ?? 0;
                return (
                  <div key={day.date} className="flex w-6 flex-col items-center gap-1" title={`${day.date}: meta ${goal}, realizado ${realized}`}>
                    <div className="flex h-24 items-end gap-0.5">
                      <div className="w-2 rounded-sm bg-slate-200" style={{ height: `${(goal / maxBar) * 100}%` }} />
                      <div className="w-2 rounded-sm bg-brand-600" style={{ height: `${(realized / maxBar) * 100}%` }} />
                    </div>
                    <span className="text-[9px] text-slate-400">{day.date.slice(8)}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Últimos meses</p>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="text-left text-xs uppercase text-slate-500">
                  <tr>
                    <th className="py-1 pr-3">Mês</th>
                    {GOAL_KINDS.map((kind) => (
                      <th key={kind} className="py-1 pr-3">{goalKindLabel(kind)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.history.map((row) => (
                    <tr key={row.month} className="border-t border-slate-100">
                      <td className="py-1.5 pr-3">{monthTitle(row.month)}</td>
                      {row.kinds.map((cell) => (
                        <td key={cell.kind} className="py-1.5 pr-3 tabular-nums">
                          {formatNumber(cell.realized)}/{formatNumber(cell.goal)} · {formatPercent(cell.percent, 0)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {!loading && data && (
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="mb-3 flex flex-wrap items-end gap-2">
            <p className="mr-auto text-xs font-semibold uppercase tracking-wide text-slate-500">Publicações do realizado</p>
            <select className="rounded-lg border border-slate-200 px-2 py-1 text-sm" value={kindFilter} onChange={(e) => setKindFilter(e.target.value)}>
              <option value="all">Todos os tipos</option>
              {GOAL_KINDS.map((kind) => (
                <option key={kind} value={kind}>{goalKindLabel(kind)}</option>
              ))}
            </select>
            <select className="rounded-lg border border-slate-200 px-2 py-1 text-sm" value={weekFilter} onChange={(e) => setWeekFilter(e.target.value)}>
              <option value="all">Todas as semanas</option>
              {data.weeks.map((week) => (
                <option key={week.index} value={String(week.index)}>{week.label}</option>
              ))}
            </select>
          </div>
          {publications.length === 0 ? (
            <p className="text-sm text-slate-500">Nenhuma publicação neste filtro.</p>
          ) : (
            <ul className="flex flex-col gap-2">
              {publications.map((row) => (
                <li key={`${row.source}-${row.id}`} className="flex flex-wrap items-center justify-between gap-2 text-sm">
                  <span className="text-slate-700">
                    {row.date.split("-").reverse().join("/")} · {goalKindLabel(row.kind)} · {row.caption}
                    {row.source === "manual" ? ` · lançamento manual${row.createdByName ? ` (${row.createdByName})` : ""}` : ""}
                  </span>
                  {row.permalink && (
                    <a href={row.permalink} target="_blank" rel="noreferrer" className="text-brand-700">
                      Abrir
                    </a>
                  )}
                </li>
              ))}
            </ul>
          )}

          <form
            className="mt-4 grid grid-cols-1 gap-2 border-t border-slate-100 pt-4 sm:grid-cols-[140px_160px_1fr_auto]"
            onSubmit={(e) => {
              e.preventDefault();
              setSavingManual(true);
              void saveManualGoalEntry({ kind: manualKind, publishedOn: manualDate, note: manualNote })
                .then(() => {
                  setManualNote("");
                  return load(month);
                })
                .catch((err: unknown) => setError(err instanceof Error ? err.message : "Erro ao lançar."))
                .finally(() => setSavingManual(false));
            }}
          >
            <select className="h-10 rounded-lg border border-slate-200 px-2 text-sm" value={manualKind} onChange={(e) => setManualKind(e.target.value)}>
              {GOAL_KINDS.map((kind) => (
                <option key={kind} value={kind}>{goalKindLabel(kind)}</option>
              ))}
            </select>
            <input type="date" className="h-10 rounded-lg border border-slate-200 px-2 text-sm" value={manualDate} onChange={(e) => setManualDate(e.target.value)} />
            <input className="h-10 rounded-lg border border-slate-200 px-2 text-sm" placeholder="Nota do lançamento manual" value={manualNote} onChange={(e) => setManualNote(e.target.value)} />
            <Button type="submit" loading={savingManual}>Lançar ajuste</Button>
          </form>
        </div>
      )}
    </section>
  );
}
